import { FC, useEffect, useMemo, useState } from 'react';
import { Center, useToast, VStack } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { MdRefresh } from 'react-icons/md';
import { LeadDTO, deleteLead, listLeads } from '@/services/leads-service';
import { ANALYST_ROUTES } from '@bemap/utils';
import { ColumnDef } from '@tanstack/react-table';
import { PageHeading, Panel, Table, TableRowActions } from '@bemap/ui';
import { useTranslation } from 'react-i18next';

const LeadsPage: FC = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [items, setItems] = useState<LeadDTO[]>([]);

  const filtered = items;

  async function load() {
    setIsLoading(true);
    try {
      const res = await listLeads();
      setItems((res as any).message || []);
    } catch (e) {
      setItems([]);
      toast({ status: 'error', title: t('general.error') || 'Error' });
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  const onDelete = async (id: number) => {
    try {
      await deleteLead(id);
      setItems(prev => prev.filter(x => x.id !== id));
      toast({ status: 'success', title: t('general.deleted') || 'Deleted' });
    } catch (e) {
      toast({ status: 'error', title: t('general.error') || 'Error' });
    }
  };

  const columns = useMemo<ColumnDef<LeadDTO, any>[]>(() => [
    {
      accessorKey: 'name',
      id: 'name',
      header: t('general.name') || 'Name',
      cell: ({ row }) => row.original.name,
    },
    {
      accessorKey: 'email',
      id: 'email',
      header: 'Email',
      cell: ({ row }) => row.original.email,
    },
    {
      accessorKey: 'phone',
      id: 'phone',
      header: t('general.phone') || 'Phone',
      cell: ({ row }) => row.original.phone || '-',
    },
    {
      accessorKey: 'created_at',
      id: 'created_at',
      header: t('analyst.assesments_table_created') || 'Created',
      cell: ({ row }) => row.original.created_at ? new Date(row.original.created_at).toLocaleString() : '-',
    },
    {
      accessorKey: 'actions',
      id: 'actions',
      header: t('table.actions') || 'Actions',
      enableColumnFilter: false,
      cell: ({ row }) => (
        <TableRowActions
          editAction={() => navigate(`${ANALYST_ROUTES.LEADS}/${row.original.id}`)}
          deleteAction={() => onDelete(row.original.id)}
        />
      ),
    },
  ], [t, navigate, items]);

  const actions = [
    {
      label: t('actions.refresh') || 'Refresh',
      icon: <MdRefresh />,
      colorScheme: 'blue',
      variant: 'outline',
      onClick: load,
    },
  ];

  if (isLoading) {
    return (
      <Center h="400px" />
    );
  }

  return (
    <VStack w="100%" gap={4} align="stretch">
      <PageHeading title={t('analyst.leads') || 'Leads'} actions={actions} />
      <Panel>
        <VStack align="stretch" gap={3}>
          <Table columns={columns} data={filtered} />
        </VStack>
      </Panel>
    </VStack>
  );
};

export default LeadsPage;


